import {
  type SurveyProductionRateAssumptionDto,
  type SurveyProductionRateAssumptionsDto,
  SurveyProductionRatePrimaryConstraint,
  type SurveySelectDto,
} from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { ExternalLink } from '@components/shared/external-link.component';
import { TextWithLabel } from '@components/shared/text-with-label.component';
import { Tile } from '@components/shared/tile.component';
import { Pencil } from 'lucide-react';
import { type FC, type ReactNode, useState } from 'react';
import { MrlProductionRateModal } from './mrl-production-rate-modal.component';

type MrlProductionRateTileProps = {
  canEdit: boolean;
  isLoading?: boolean;
  survey: SurveySelectDto | null;
};

const primaryConstraintLabels: Record<SurveyProductionRatePrimaryConstraint, string> = {
  [SurveyProductionRatePrimaryConstraint.Facility]: 'Facility',
  [SurveyProductionRatePrimaryConstraint.Funding]: 'Funding',
  [SurveyProductionRatePrimaryConstraint.Materials]: 'Materials',
  [SurveyProductionRatePrimaryConstraint.SubTier]: 'Sub-tier',
  [SurveyProductionRatePrimaryConstraint.Tooling]: 'Tooling',
  [SurveyProductionRatePrimaryConstraint.Workforce]: 'Workforce',
};

const assumptionLabels: Record<keyof SurveyProductionRateAssumptionsDto, string> = {
  criticalMaterials: 'Critical materials',
  facilityTooling: 'Facility and tooling',
  funding: 'Funding',
  regulatory: 'Regulatory',
  supplyChain: 'Supply chain',
  workforce: 'Workforce',
};

const assumptionTooltips: Record<keyof SurveyProductionRateAssumptionsDto, string> = {
  criticalMaterials: 'Availability confirmed (alloys, semiconductors, energetics, rare earths)',
  facilityTooling: 'No new capex required',
  funding: 'No CR, stop-work, or de-obligation assumed',
  regulatory: 'ITAR/EAR/export licenses and permits current',
  supplyChain: 'All tiers identified, no single-source risk',
  workforce: 'Skilled and cleared to required level',
};

const assumptionKeys: Array<keyof SurveyProductionRateAssumptionsDto> = [
  'supplyChain',
  'criticalMaterials',
  'workforce',
  'facilityTooling',
  'funding',
  'regulatory',
];

const valueLabelClassName = 'flex min-h-8 items-end text-xs leading-4';

const formatNumber = (value: number, unit: string): string => `${value} ${unit}`;

const renderAssumptionLabel = (key: keyof SurveyProductionRateAssumptionsDto): ReactNode => {
  return (
    <span className="inline-flex items-center gap-1">
      {assumptionLabels[key]}
      <TooltipComponent
        className="text-xs whitespace-pre-wrap"
        side="top"
        trigger={
          <span className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-[10px] font-medium leading-none text-gray-500">
            ?
          </span>
        }
      >
        {assumptionTooltips[key]}
      </TooltipComponent>
    </span>
  );
};

const getEvidenceUrl = (value: string): string | null => {
  const trimmedValue = value.trim();
  if (!trimmedValue || trimmedValue.includes(' ')) return null;

  const urlPattern = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(?::\d+)?(\/\S*)?$/i;
  if (!urlPattern.test(trimmedValue)) return null;

  return /^https?:\/\//i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`;
};

const renderAssumptionValue = (assumption: SurveyProductionRateAssumptionDto): ReactNode => {
  if (assumption.inPlace) return 'Yes';
  if (!assumption.qualifier) return 'No';

  return (
    <div className="flex flex-col gap-0.5">
      <span>No</span>
      <span className="text-xs font-normal leading-4 text-gray-500">{assumption.qualifier}</span>
    </div>
  );
};

const renderEvidence = (evidence: string): ReactNode => {
  const evidenceUrl = getEvidenceUrl(evidence);
  if (!evidenceUrl) return evidence;

  return (
    <ExternalLink
      className="max-w-full text-sm font-medium"
      href={evidenceUrl}
    >
      {evidence}
    </ExternalLink>
  );
};

const ProductionRateSection: FC<{ children: ReactNode; title: string }> = (props) => {
  const { children, title } = props;

  return (
    <div className="flex flex-col gap-1.5 rounded-lg bg-gray-100 p-3">
      <div className="text-sm font-medium text-gray-700">{title}</div>
      <div className="grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2">{children}</div>
    </div>
  );
};

export const MrlProductionRateTile: FC<MrlProductionRateTileProps> = (props) => {
  const { canEdit, isLoading, survey } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const productionRate = survey?.productionRate ?? null;
  const canOpenModal = Boolean(canEdit && survey?.id);

  const openModal = (): void => {
    if (!canOpenModal) return;
    setIsModalOpen(true);
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Tile
        emptyState={{
          message: canOpenModal ? 'Add Production Rate' : undefined,
          onClick: canOpenModal ? openModal : undefined,
          title: 'No Production Rate',
        }}
        header={{
          iconButton:
            canOpenModal && productionRate
              ? {
                  icon: <Pencil />,
                  onClick: openModal,
                }
              : undefined,
          title: 'Production Rate',
        }}
        hoverReveal
        isEmpty={!productionRate}
        isLoading={isLoading}
      >
        {productionRate && (
          <div className="flex flex-col gap-4">
            <ProductionRateSection title="Low Rate Production">
              <TextWithLabel
                className="flex-col-reverse text-sm"
                label="Rate (units/month)"
                labelClassName={valueLabelClassName}
                title={formatNumber(productionRate.lowRateProduction.rateUnitsPerMonth, 'units/month')}
              />
              <TextWithLabel
                className="flex-col-reverse text-sm"
                label="Duration sustained (months)"
                labelClassName={valueLabelClassName}
                title={formatNumber(productionRate.lowRateProduction.durationMonths, 'months')}
              />
              <TextWithLabel
                className="flex-col-reverse text-sm sm:col-span-2"
                label="Evidence"
                labelClassName="text-xs"
                title={renderEvidence(productionRate.lowRateProduction.evidence)}
              />
            </ProductionRateSection>

            <ProductionRateSection title="Full Rate Production">
              <TextWithLabel
                className="flex-col-reverse text-sm"
                label="Rate (units/month)"
                labelClassName={valueLabelClassName}
                title={formatNumber(productionRate.fullRateProduction.rateUnitsPerMonth, 'units/month')}
              />
              <TextWithLabel
                className="flex-col-reverse text-sm"
                label="Order-to-delivery lead time (weeks)"
                labelClassName={valueLabelClassName}
                title={formatNumber(productionRate.fullRateProduction.leadTimeWeeks, 'weeks')}
              />
              <TextWithLabel
                className="flex-col-reverse text-sm sm:col-span-2"
                label="Evidence"
                labelClassName="text-xs"
                title={renderEvidence(productionRate.fullRateProduction.evidence)}
              />
            </ProductionRateSection>

            <ProductionRateSection title="Surge Production">
              <TextWithLabel
                className="flex-col-reverse text-sm"
                label="Peak rate (units/month)"
                labelClassName={valueLabelClassName}
                title={formatNumber(productionRate.surgeProduction.peakRateUnitsPerMonth, 'units/month')}
              />
              <TextWithLabel
                className="flex-col-reverse text-sm"
                label="Ramp time from FRP (weeks)"
                labelClassName={valueLabelClassName}
                title={formatNumber(productionRate.surgeProduction.rampTimeWeeks, 'weeks')}
              />
              <TextWithLabel
                className="flex-col-reverse text-sm"
                label="Primary constraint"
                labelClassName={valueLabelClassName}
                title={primaryConstraintLabels[productionRate.surgeProduction.primaryConstraint]}
              />
              <TextWithLabel
                className="flex-col-reverse text-sm"
                label="Evidence or basis"
                labelClassName={valueLabelClassName}
                title={renderEvidence(productionRate.surgeProduction.evidence)}
              />
            </ProductionRateSection>

            <ProductionRateSection title="Assumptions">
              {assumptionKeys.map((key) => {
                const assumption = productionRate.assumptions[key];

                return (
                  <TextWithLabel
                    className="flex-col-reverse text-sm"
                    key={key}
                    label={renderAssumptionLabel(key)}
                    labelClassName="text-xs"
                    title={renderAssumptionValue(assumption)}
                  />
                );
              })}
            </ProductionRateSection>
          </div>
        )}
      </Tile>

      {canOpenModal && survey?.id && (
        <MrlProductionRateModal
          isOpen={isModalOpen}
          onClose={closeModal}
          productionRate={productionRate}
          surveyId={survey.id}
        />
      )}
    </>
  );
};
